import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ProfileCard from "../common/profileCard";
import StudentSidebar from "../common/studentSidebar";
import { Editor as TinyMCEEditor } from "tinymce";
import { useState, useEffect, useCallback, useRef } from "react";
import { CustomEditor } from "../../../core/common/ults/editor";
import { useHandleAssign } from "../../../core/hooks/useHandleAssign";
import {
  convertTimestampToDate,
  getRemainingTime,
} from "../../../core/common/ults/dateTime";
import { Link, useLocation, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, Button } from "antd";

interface StudentFile extends File {
  filename?: string;
}

interface Plugin {
  type: string;
  name: string;
  fileareas?: Array<{ area: string; files: any[] }>;
  editorfields?: Array<{
    name: string;
    description: string;
    text: string;
    format: number;
    itemid: number;
  }>;
}

const StudentAssignQuestion = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { assignDuedate, cmid, quizName, introAssign, filesArray = [] } = location.state || {};
  const [content, setContent] = useState("");
  const [initialText, setInitialText] = useState("");
  const [files, setFiles] = useState<StudentFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedMediaFiles, setUploadedMediaFiles] = useState<any[]>([])
  const assignId = Number(useParams().id);
  const route = all_routes;
  const {
    statusSubmissions,
    saveAssign,
  } = useHandleAssign(assignId);
  const { assignInfo } = useHandleAssign(cmid);
  const { uploadAssignMutation } = useHandleAssign(assignInfo?.contextid);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    loading: boolean;
  } | null>(null);


  const handleEditorChange = (newContent: string, editor: TinyMCEEditor) => {
    setContent(newContent);
  };
  const plugins = statusSubmissions?.lastattempt?.submission?.plugins || [];
  const onlineTextPlugin = plugins.find((p: Plugin) => p.type === "onlinetext");
  const filePlugin = plugins.find((p: Plugin) => p.type === "file");
  const hasOnlineTextPlugin = !!onlineTextPlugin;
  const hasFilePlugin = !!filePlugin;
  const maxSizeBytes = filePlugin?.maxsize ?? 0;
  const maxSizeMB = maxSizeBytes / 1024 / 1024;
  const maxFiles = filePlugin?.maxfiles;
  const isAssignmentExpired =
    !!assignDuedate && assignDuedate > 0 && Math.floor(Date.now() / 1000) > assignDuedate;
  const canAddMoreFiles =
    !isAssignmentExpired && (!maxFiles || files.length < maxFiles);


  const format = onlineTextPlugin?.editorfields?.[0]?.format;

  const text = onlineTextPlugin?.editorfields?.find(
    (field: any) => field.name === "onlinetext"
  )?.text;

  const submittedFiles =
    statusSubmissions?.lastattempt?.submission?.plugins?.find(
      (p: any) => p.type === "file"
    )?.fileareas?.[0]?.files || [];

  useEffect(() => {
    if (text && !content && !initialText) {
      setContent(text);
      setInitialText(text);
    }
  }, [text]);

  // Lắng nghe event khi insert media từ recorder để lấy response upload
  useEffect(() => {
    const handleInsertMedia = (event: any) => {
      const { uploadedFile, type } = event.detail;
      
      if (uploadedFile) {
        // Lưu response vào state
        setUploadedMediaFiles(prev => [...prev, {
          ...uploadedFile,
          type: type,
          timestamp: Date.now(),
        }]);
        
      }
    };

    window.addEventListener('tinymce-insert-media', handleInsertMedia);

    return () => {
      window.removeEventListener('tinymce-insert-media', handleInsertMedia);
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (newFiles: FileList | File[]) => {
      if (isAssignmentExpired) return;

      const incomingFiles = Array.from(newFiles);
      if (incomingFiles.length === 0) return;

      for (const file of incomingFiles) {
        if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
          alert(`${t("assignment.fileSizeError")} ${maxSizeMB}MB`);
          return;
        }
      }

      setFiles((prev) => {
        if (maxFiles && prev.length + incomingFiles.length > maxFiles) {
          alert(`${t("assignment.maxFilesError")} ${maxFiles}`);
          return prev;
        }
        return [...prev, ...incomingFiles];
      });
    },
    [t, maxSizeBytes, maxSizeMB, maxFiles, isAssignmentExpired]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
      e.target.value = ""; // reset value
    }
  };
  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // reset value
    }
  };
  const handleSaveSubmission = async () => {
    if (isAssignmentExpired) return;

    if (!content.trim() && files.length === 0 && submittedFiles.length === 0) {
      toast.error(
        <>
          <div><b>{t("assignment.errorTitle")}</b></div>
          <div>{t("assignment.requireContentOrFile")}</div>
        </>
      );
      return;
    }

    let uploadedItemId = 0;

    if (files.length > 0) {
      try {
        for (const selectedFile of files) {
          const uploadParams = {
            contextid: assignInfo?.contextid,
            component: "mod_assign",
            filearea: "submission_files",
            itemid: uploadedItemId,
            filepath: "/",
            filename: selectedFile.name,
            file: selectedFile,
          };
          const result: any = await uploadAssignMutation.mutateAsync(uploadParams);
          if (Array.isArray(result) && result.length > 0 && result[0]?.itemid) {
            uploadedItemId = result[0].itemid;
          } else {
            toast.error(
              <>
                <div><b>{t("assignment.errorTitle")}</b></div>
                <div>{t("assignment.uploadError")}</div>
              </>
            );
            return;
          }
        }
      } catch (error) {
        toast.error(
          <>
            <div><b>{t("assignment.errorTitle")}</b></div>
            <div>{t("assignment.uploadError")}</div>
          </>
        );
        return;
      }
    }

    const mediaItemId = uploadedMediaFiles.length > 0 ? uploadedMediaFiles[0].itemid : 0;

    // Thay thế blob URLs thành @@PLUGINFILE@@/filename và xóa data attributes
    let processedContent = content;
    if (uploadedMediaFiles.length > 0) {
      // Parse content
      const parser = new DOMParser();
      const doc = parser.parseFromString(processedContent, 'text/html');
      
      uploadedMediaFiles.forEach((mediaFile) => {
        const filename = mediaFile.filename;
        
        // Tìm tất cả audio/video elements có blob URL
        const audioElements = doc.querySelectorAll('audio[src^="blob:"]');
        const videoElements = doc.querySelectorAll('video[src^="blob:"]');
        const allMediaElements = [...Array.from(audioElements), ...Array.from(videoElements)];
        
        allMediaElements.forEach((element) => {
          // Kiểm tra xem element này có thuộc về filename hiện tại không
          // Cách 1: Kiểm tra data-filename trực tiếp
          let elementFilename = element.getAttribute('data-filename');
          
          // Cách 2: Kiểm tra parent span.mce-preview-object
          if (!elementFilename) {
            const parentSpan = element.closest('span.mce-preview-object');
            if (parentSpan) {
              elementFilename = parentSpan.getAttribute('data-mce-p-data-filename');
            }
          }
          
          // Nếu filename khớp, thay thế blob URL
          if (elementFilename === filename) {
            const newSrc = `@@PLUGINFILE@@/${filename}`;
            element.setAttribute('src', newSrc);
            
            // Xóa tất cả data attributes
            const attributesToRemove = [
              'data-moodle-file',
              'data-contextid',
              'data-component',
              'data-filearea',
              'data-itemid',
              'data-filepath',
              'data-filename'
            ];
            
            attributesToRemove.forEach(attr => {
              if (element.hasAttribute(attr)) {
                element.removeAttribute(attr);
              }
            });
          }
        });
      });
      
      // Serialize lại thành HTML
      processedContent = doc.body.innerHTML;
    }

    const saveParams = {
      assignId,
      onlinetextContent: processedContent, // Sử dụng content đã được xử lý
      onlinetextFormat: format ?? 1,
      onlinetextItemid: mediaItemId, // Sử dụng itemid từ media files
      ...(uploadedItemId > 0 && { fileUploadId: uploadedItemId }),
    };
    if (uploadedItemId > 0) {
      saveParams.fileUploadId = uploadedItemId;
    }

    saveAssign.mutate(saveParams, {
      onSuccess(data) {
        if (data?.exception || data?.errorcode || data?.message) {
          toast.error(
            <>
              {/* <div><b>{t("assignment.errorTitle")}</b></div> */}
              <div>{t("assignment.submitError")}</div>
            </>
          );
        } else {
          toast.success(
            <>
              {/* <div><b>{t("assignment.successTitle")}</b></div> */}
              <div>{t("assignment.saveSuccess")}</div>
            </>
          );
          setFiles([]);
          setInitialText(content.trim());
          setIsDragging(false); // ✅ Đóng ô nhập file lại
        }
      },
      onError(error) {
        toast.error(
          <>
            <div><b>{t("assignment.errorTitle")}</b></div>
            <div>{t("assignment.submitError")}</div>
          </>
        );
      },
    });
  };
  const isChanged =
    (content.trim() !== initialText.trim()) || files.length > 0;

  return (
    <div className="content">
      <div className="container">
        <ProfileCard />
        <div className="row">
          <StudentSidebar />
          <div className="col-lg-9">
            <Breadcrumb title={t("assignment.assignmentDetail")} />
            <h5 className="py-3">{t("assignment.myAssignAttempts")}</h5>
            <div className="card">
              <div className="ps-4 pt-4">
                <h6 className="">{quizName}</h6>
                <p
                  className="fs-14"
                  dangerouslySetInnerHTML={{
                    __html: introAssign,
                  }}
                />
                {filesArray.length > 0 && (
                  <div className="align-center">
                    <p className="mb-2">{t("assignment.attachmentFile")}</p>
                    {filesArray.map((f: any, idx: number) => (
                      <Button
                        key={idx}
                        type="link"
                        onClick={() => {
                          const fileUrlWithToken = `${f.fileurl}?token=${sessionStorage.getItem("wstoken")}`;
                          const extension = f.filename.split(".").pop()?.toLowerCase();
                          const isImage = ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(extension || "");
                          const isPdf = extension === "pdf";
                          const isWord = ["doc", "docx"].includes(extension || "");
                          const viewerUrl = isWord
                            ? `https://docs.google.com/gview?url=${encodeURIComponent(fileUrlWithToken)}&embedded=true`
                            : fileUrlWithToken;

                          setPreviewFile({
                            url: viewerUrl,
                            name: f.filename,
                            isImage,
                            isPdf,
                            isWord,
                            loading: false,
                          });
                        }}
                        className="d-flex align-items-center p-0 gap-0"
                      >
                        <i className="fas fa-file me-2"></i>
                        <span>{f.filename}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: "200px", backgroundColor: "#f8f9fa" }}>
                          {t("assignment.submitStatus")}
                        </th>
                        <td>
                          {statusSubmissions?.lastattempt?.submission?.status === "submitted"
                            ? t("assignment.submitted")
                            : t("assignment.notSubmitted")}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.gradeStatus")}</th>
                        <td>
                          {statusSubmissions?.lastattempt?.gradingstatus === "graded"
                            ? t("assignment.graded")
                            : t("assignment.notGraded")}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.duedate")}</th>
                        <td>{convertTimestampToDate(assignDuedate)}</td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.remainingTime")}</th>
                        <td>{getRemainingTime(assignDuedate)}</td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.lastModified")}</th>
                        <td>{convertTimestampToDate(Number(statusSubmissions?.lastattempt?.submission?.timemodified))}</td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.onlineText")}</th>
                        <td>
                          {hasOnlineTextPlugin ? (
                            <div className="mb-3">
                              <CustomEditor 
                                value={content} 
                                onEditorChange={handleEditorChange} 
                                contextid={assignInfo?.contextid}
                                disabled={isAssignmentExpired}
                              />
                            </div>
                          ) : (
                            <div className="text-muted">{t("assignment.onlineTextNotAvailable")}</div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa" }}>{t("assignment.submissionFiles")}</th>
                        <td>
                          {hasFilePlugin ? (
                            <div className="file-upload-area">
                              <div className="file-list">
                                {(files.length > 0 ? files : submittedFiles).map((file: any, index: number) => (
                                  <div
                                    key={index}
                                    className="file-item d-flex align-items-center justify-content-between p-2 border-bottom"
                                  >
                                    <a
                                      href={files.length > 0 ? "#" : `${file.fileurl}?token=${sessionStorage.getItem("wstoken")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-flex align-items-center btn btn-outline-secondary"
                                      download={files.length === 0 ? file.filename : undefined}
                                    >
                                      <i className="fas fa-file me-2"></i>
                                      <span>{files.length > 0 ? file.name : file.filename}</span>
                                    </a>
                                    {files.length > 0 && !isAssignmentExpired && (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => removeFile(index)}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {submittedFiles.length > 0 && files.length === 0 ? (
                                <>
                                  {!isDragging && !files.length && !isAssignmentExpired && (
                                    <button
                                      className="btn btn-primary mt-3 ms-2"
                                      onClick={() => setIsDragging(true)}
                                    >
                                      {t("assignment.editSubmission")}
                                    </button>
                                  )}
                                  {isDragging && canAddMoreFiles && (
                                    <div
                                      className="drop-zone mt-3 p-4 text-center border rounded"
                                      onDragOver={handleDragOver}
                                      onDragLeave={handleDragLeave}
                                      onDrop={handleDrop}
                                    >
                                      <div className="text-center">
                                        <i className="fas fa-arrow-down mb-2"></i>
                                        <p>{t("assignment.dragDropText")}</p>
                                        <input
                                          ref={fileInputRef}
                                          type="file"
                                          multiple
                                          onChange={handleFileInput}
                                          className="d-none"
                                        />
                                        <button
                                          className="btn btn-primary"
                                          onClick={() => fileInputRef.current?.click()}
                                        >
                                          {t("assignment.selectFiles")}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (files.length > 0 || submittedFiles.length === 0) && canAddMoreFiles ? (
                                <div
                                  className={`drop-zone mt-3 p-4 text-center border rounded ${isDragging ? "bg-light" : ""}`}
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                >
                                  <div className="text-center">
                                    <i className="fas fa-arrow-down mb-2"></i>
                                    <p>{t("assignment.dragDropText")}</p>
                                    <input
                                      ref={fileInputRef}
                                      type="file"
                                      multiple
                                      onChange={handleFileInput}
                                      className="d-none"
                                    />
                                    <button
                                      className="btn btn-primary"
                                      onClick={() => fileInputRef.current?.click()}
                                    >
                                      {t("assignment.selectFiles")}
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <span className="text-muted">{`${t("assignment.fileSizeLimits")} ${maxSizeMB}MB, ${t("assignment.fileNumberLimits")} ${maxFiles}`}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted">{t("assignment.fileSubmissionNotAvailable")}</div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between mt-4">
              <Link to={route.studentQuiz} className="btn btn-secondary">
                {t("common.cancel")}
              </Link>
              <button
                className="btn btn-primary"
                disabled={saveAssign.isPending || !isChanged || isAssignmentExpired}
                onClick={handleSaveSubmission}
              >
                {saveAssign.isPending ? t("assignment.saving") : t("assignment.saveChanges")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title={previewFile?.name}
        open={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width="90vw"
        style={{ top: '45%', transform: 'translateY(-50%)' }}
        bodyStyle={{ height: '70vh', padding: 0 }}
        centered
      >
        {previewFile && (
          <>
            {!previewFile.loading && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                <span>{t("assignment.loadingFile")}</span>
              </div>
            )}
            {previewFile.isImage && (
              <img
                src={previewFile.url}
                alt={previewFile.name}
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                onLoad={() => setPreviewFile((prev) => prev && { ...prev, loading: true })}
                onError={() => {
                  toast.error(
                    <>
                      <div><b>{t("assignment.errorTitle")}</b></div>
                      <div>{t("assignment.cannotLoadImage")}</div>
                    </>
                  );
                  setPreviewFile(null);
                }}
              />
            )}
            {previewFile.isPdf && (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                style={{ border: "none", minHeight: '100%', width: '100%' }}
                onLoad={() => setPreviewFile((prev) => prev && { ...prev, loading: true })}
                onError={() => {
                  toast.error(
                    <>
                      <div><b>{t("assignment.errorTitle")}</b></div>
                      <div>{t("assignment.cannotLoadFile")}</div>
                    </>
                  );
                  setPreviewFile(null);
                }}
              />
            )}
            {previewFile.isWord && (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                style={{ border: "none", minHeight: '100%', width: '100%' }}
                onLoad={() => setPreviewFile((prev) => prev && { ...prev, loading: true })}
                onError={() => {
                  toast.error(
                    <>
                      <div><b>{t("assignment.errorTitle")}</b></div>
                      <div>{t("assignment.cannotLoadFile")}</div>
                    </>
                  );
                  setPreviewFile(null);
                }}
              />
            )}
          </>
        )}
      </Modal>

    </div>
  );
};

export default StudentAssignQuestion;